import React, { ReactNode, useEffect, useMemo, useState } from 'react';

import { AppLayout, Button, Container, Header, Link, Spinner } from '@amzn/awsui-components-react';
import { Auth, Hub } from 'aws-amplify';
import { useTranslation } from 'react-i18next';
import { Outlet, useSearchParams, useLocation } from 'react-router-dom';
import { BottomNav } from './components/bottomNav';
import KatalMetrics from './components/katalAnalytics';
import { LocalizationProvider } from './components/localization/localizationProvider';
import { ResetQueriesOnPageChange } from './components/queryResetter';
import { ErrorBoundary } from './components/errorBoundary';
import { TopNav } from './components/topNav';
import { NewTopNav } from './components/newTopNav';
import { SiteNavigation } from './components/siteNav';
import { SiteBreadcrumbs } from './components/siteBreadcrumbs';

import './App.scss';
import { GateOnFeatureLoad, GateOnUserLoad } from './components/context';
import { externalLinks } from './data/externalLinks';
import { LocalizedTrans } from './hooks/localization/localizedTrans';
import { useLocalizedText } from './hooks/localizedText';
import { POST_LOGIN_PATH_KEY } from './hooks/login/login';
import { NAVIGATION_REDESIGN, UNAUTHORIZED } from './utils/constants';
import { featureIsEnabled, useFeatures } from './hooks/features';
import { useFeatureFlagLoadMetrics } from './hooks/useFeatureFlagLoadMetrics';

/**
 * Both screens below sit outside LocalizationProvider, which is mounted for authorized users further
 * down, so no translated text can reach them and they read the English source however they ask for it.
 * Localizing them means giving the pre-authentication shell a provider of its own, seeded from the
 * browser's language rather than the user's saved preference; until then, English is the only option.
 */

export function LoadSsoButton() {
  // Deliberately the plain i18next reader: this screen renders once signing in has already failed, and
  // reading text through the Localization flag would query features, whose token lookup attempts the
  // sign-in a second time. Nothing is lost, since neither reader can produce anything but English here.
  const { t } = useTranslation();
  return (
    <Container data-testid="loading-spinner" className="fa-3x">
      <Header>{t('auth.title')}</Header>
      <Button
        variant="primary"
        onClick={() =>
          Auth.federatedSignIn({
            customProvider: 'AmazonFederate',
            customState: window.location.href,
          })
        }
      >
        {t('auth.login')}
      </Button>
    </Container>
  );
}

function UnauthorizedUser() {
  // This user is signed in, only not allowed in, so reading the flag here costs a features query and
  // no more. The sentence below renders through react-i18next until the pre-authentication shell has
  // a provider of its own; it is wired to <LocalizedTrans> so it needs no further change when it does.
  const { t } = useLocalizedText(['translation']);

  return (
    <Container data-testid="unauthorized">
      <Header>{t('auth.failed')}</Header>
      <p>
        <LocalizedTrans
          i18nKey="auth.failedInfo"
          components={{
            wikiLink: (
              <Link
                external
                variant="primary"
                data-aci-analytics-name={UNAUTHORIZED + '_wikiLink'}
                href={externalLinks.support.whoCanAccess}
              />
            ),
            contactLink: (
              <Link
                external
                variant="primary"
                data-aci-analytics-name={UNAUTHORIZED + '_contactLink'}
                href={externalLinks.support.contact}
              />
            ),
          }}
        />
      </p>
    </Container>
  );
}

function Loading() {
  return (
    <Container data-testid="loading-spinner" className="fa-3x">
      <Spinner />
    </Container>
  );
}

function LegacyLayout() {
  const location = useLocation();
  return (
    <div className="App" data-testid="app-container">
      <div className="header">
        <KatalMetrics />
        <TopNav />
      </div>
      <main>
        <ResetQueriesOnPageChange />
        <ErrorBoundary resetKey={location.key}>
          <Outlet />
        </ErrorBoundary>
      </main>
      <BottomNav />
    </div>
  );
}

export interface AppOutletContext {
  setTools: (content: ReactNode) => void;
  setToolsOpen: (open: boolean) => void;
  setBreadcrumbOverrides: (overrides: Record<string, string>) => void;
  /**
   * Page-level notifications (Flashbar) rendered in the AppLayout notifications region.
   * The legacy per-page AppLayouts each had their own notifications slot; pages that migrate to
   * the shared layout push their Flashbar here instead so results like bulk-upload success/failure
   * counts still surface (CR-291797692 rev 8). Pass null to clear on unmount.
   */
  setNotifications: (content: ReactNode) => void;
  /**
   * Navigation guard for unsaved-changes protection (Cloudscape pattern).
   * Pages with unsaved state set a guard callback that receives the target href.
   * Return true to allow navigation, false to block it (and show a confirmation modal).
   * SiteBreadcrumbs and SiteNavigation call this before navigating.
   */
  setNavigationGuard: (guard: ((href: string) => boolean) | null) => void;
}

// Cloudscape multipage-create pattern (https://cloudscape.design/patterns/resource-management/create/multi-page-create/):
// wizard-based pages use contentType="wizard", form-based pages use contentType="form".
// Both apply the recommended max content width and close the side nav by default.
const WIZARD_PAGES = ['/create-event', '/edit-event'];
const FORM_PAGES = ['/track-attendance'];

function NewNavigationLayout() {
  const { t } = useTranslation();
  const location = useLocation();
  const [tools, setTools] = useState<ReactNode>(null);
  const [toolsOpen, setToolsOpen] = useState(false);
  const [breadcrumbOverrides, setBreadcrumbOverrides] = useState<Record<string, string>>({});
  const [notifications, setNotifications] = useState<ReactNode>(null);
  const [navigationGuard, setNavigationGuard] = useState<((href: string) => boolean) | null>(null);

  const contentType = WIZARD_PAGES.includes(location.pathname)
    ? 'wizard'
    : FORM_PAGES.includes(location.pathname)
      ? 'form'
      : 'default';

  const outletContext: AppOutletContext = useMemo(
    () => ({
      setTools,
      setToolsOpen,
      setBreadcrumbOverrides,
      setNotifications,
      // Wrap in functional-update form: React's useState setter treats a raw function argument
      // as an updater callback. Without this wrapper, setNavigationGuard(myGuard) would invoke
      // myGuard(prevState) instead of storing the function as the new value.
      setNavigationGuard: (guard: ((href: string) => boolean) | null) =>
        setNavigationGuard(() => guard),
    }),
    [setTools, setToolsOpen, setBreadcrumbOverrides, setNotifications]
  );

  return (
    <div className="App" data-testid="app-container">
      <div
        id="top-nav"
        data-testid="top-navigation"
        style={{ position: 'sticky', top: 0, zIndex: 1000 }}
      >
        <KatalMetrics />
        <NewTopNav navigationGuard={navigationGuard} />
      </div>
      <AppLayout
        ariaLabels={{
          navigation: t('appLayout.navigation'),
          navigationClose: t('appLayout.navigationClose'),
          navigationToggle: t('appLayout.navigationToggle'),
          tools: t('appLayout.tools'),
          toolsClose: t('appLayout.toolsClose'),
          toolsToggle: t('appLayout.toolsToggle'),
        }}
        contentType={contentType}
        navigation={<SiteNavigation navigationGuard={navigationGuard} />}
        breadcrumbs={
          location.pathname === '/' ? undefined : (
            <SiteBreadcrumbs overrides={breadcrumbOverrides} navigationGuard={navigationGuard} />
          )
        }
        maxContentWidth={Number.MAX_VALUE} // Let content fill available width (no Cloudscape max-width cap)
        notifications={notifications}
        // Keep results visible while scrolling long tables (e.g. bulk-upload success/failure counts
        // on a large signups list), matching Cloudscape's notification guidance.
        stickyNotifications
        content={
          <div className="new-nav-content">
            <ResetQueriesOnPageChange />
            <ErrorBoundary resetKey={location.key}>
              <Outlet context={outletContext} />
            </ErrorBoundary>
          </div>
        }
        tools={tools}
        toolsOpen={toolsOpen}
        onToolsChange={({ detail }) => setToolsOpen(detail.open)}
        toolsHide={!tools} // Hide the tools icon entirely when no panel content is set
        headerSelector="#top-nav" // Tells AppLayout to offset for the sticky top nav height
      />
    </div>
  );
}

function App() {
  // We are just hammering the issue here - possible we only need to intercept one of these events,
  // but redirecting on any of them to be sure that we do so.
  useEffect(() => {
    Hub.listen('auth', ({ payload: { event, data } }) => {
      switch (event) {
        case 'oAuthSignIn':
        case 'signIn':
          const returnPath = sessionStorage.getItem(POST_LOGIN_PATH_KEY);
          if (returnPath) {
            const urlValid = new URL(returnPath);
            window.location.assign(decodeURI(urlValid.href));
            sessionStorage.removeItem(POST_LOGIN_PATH_KEY);
          }
          break;
        default:
          return;
      }
    });
  });

  return (
    <GateOnUserLoad
      loading={() => <Loading />}
      error={() => <LoadSsoButton />}
      unauthorized={() => <UnauthorizedUser />}
      authorized={() => (
        <GateOnFeatureLoad
          loading={() => <Loading />}
          render={() => (
            <LocalizationProvider>
              <NavigationSwitch />
            </LocalizationProvider>
          )}
        />
      )}
    />
  );
}

/**
 * Renders either the new navigation layout or legacy layout based on the
 * NavigationRedesign feature flag. This component is rendered inside
 * GateOnFeatureLoad, so features are guaranteed to be loaded.
 */
function NavigationSwitch() {
  const [searchParams] = useSearchParams();
  const features = useFeatures();
  useFeatureFlagLoadMetrics(features);
  const isNewNav = featureIsEnabled(NAVIGATION_REDESIGN, features, searchParams);

  return isNewNav ? <NewNavigationLayout /> : <LegacyLayout />;
}

export default App;
